import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  useTournamentLeaderboard,
  type TournamentLeaderboardItem,
} from "@/hooks/tournaments/use-tournament-leaderboard";
import { cn } from "@/lib/utils";

import {
  LEADERBOARD_GRID_COMPACT,
  LeaderboardEmptyRow,
  LeaderboardRow,
} from "./tournament-leaderboard-table";
import { YourStandingCard } from "./your-standing-card";

/** Rows shown before "Show more", and how many each tap adds. */
const FIRST_ROWS = 10;
const MORE_ROWS = 50;
/** The dock's board opens on the podium, as the legacy overlay's mini board
 *  did: the leaders are always in view, the player's own window under them. */
const DOCK_FIRST_ROWS = 3;
/** The rows either side of the player's own when they rank below what is shown. */
const AROUND_ME = 2;

/**
 * The tournament's phone Overview: the whole leaderboard in the page, the way the missions
 * ladder is — no dialog, no inner scroll box. The player's standing leads (when they have joined),
 * then one plain board where EVERY row carries its own position and its own prize ("—" once the
 * prizes run out), so there is never a question of what a place wins, whether a prize is shared,
 * or where the prizes stop. It opens on the top ten (the podium in the overlay's dock) plus a
 * window around the player; "Show more" adds rows inline.
 */
export const TournamentBoard = ({
  campaignId,
  joined,
  density = "default",
}: {
  campaignId: string | number;
  joined: boolean;
  /** `dock`: the overlay's narrow column — the board opens on the podium
   *  instead of the top ten, with the player's own window under it, and the
   *  standing card is at its dock density. "Show more" pages the rest in as it
   *  does everywhere (the dock's body scrolls): it is what stands in for the
   *  legacy overlay's full-leaderboard view. */
  density?: "default" | "dock";
}) => {
  const { t } = useTranslation();
  const {
    fullLeaderboardData: rows,
    playerData,
    showScoreMultiplierSuffix,
    prizeForPosition,
    loading,
  } = useTournamentLeaderboard(String(campaignId));
  const scoreSuffix = showScoreMultiplierSuffix ? "x" : "";
  const dock = density === "dock";
  const firstRows = dock ? DOCK_FIRST_ROWS : FIRST_ROWS;
  const [limit, setLimit] = useState(firstRows);

  // What is shown: the first `limit` rows, plus — when the player ranks below them — a window
  // around the player after a gap. A player just below the cut simply extends it. The player's
  // row counts whether or not they have opted in: a score can put them on the board first.
  const playerRank = playerData?.position;
  const shownLimit =
    playerRank != null && playerRank <= limit + AROUND_ME + 1
      ? Math.max(limit, playerRank + AROUND_ME)
      : limit;
  const head = rows.slice(0, shownLimit);
  const around =
    playerRank != null && playerRank > shownLimit
      ? rows.slice(
          Math.max(shownLimit, playerRank - 1 - AROUND_ME),
          playerRank + AROUND_ME
        )
      : [];
  const hasMore = shownLimit < rows.length;

  const renderRow = (item: TournamentLeaderboardItem) => (
    <LeaderboardRow
      key={item.id ?? `pos-${item.position}`}
      item={item}
      scoreSuffix={scoreSuffix}
      size="compact"
      layout="grid"
    />
  );

  return (
    <div className="flex w-full flex-col gap-2 ls:col-span-2">
      {joined && <YourStandingCard campaignId={campaignId} density={density} />}
      <Card
        inset="none"
        title={t("tournament.leaderboard", "Leaderboard")}
        aside={
          rows.length > 0 ? (
            <span className="font-body text-[11px] text-muted-foreground lg:text-[12px]">
              {t("tournament.playerCount", "{{count}} players", {
                count: rows.length,
              })}
            </span>
          ) : undefined
        }
      >
        {loading ? (
          <p className="py-6 text-center font-body text-[12px] text-muted-foreground">
            {t("tournament.loadingLeaderboard", "Loading leaderboard…")}
          </p>
        ) : rows.length === 0 ? (
          // Nobody has scored yet: the ranks with their prizes, so the board still says what is
          // on offer at each place. A real table for assistive tech too (role="table" + rows).
          <div
            role="table"
            aria-label={t("tournament.leaderboard", "Leaderboard")}
            className={cn("w-full gap-y-0.5", LEADERBOARD_GRID_COMPACT)}
          >
            {Array.from({ length: firstRows }, (_, i) => (
              <LeaderboardEmptyRow
                key={i}
                rank={i + 1}
                prize={prizeForPosition(i + 1)}
                size="compact"
                layout="grid"
                nameVisible={false}
              />
            ))}
          </div>
        ) : (
          <div
            role="table"
            aria-label={t("tournament.leaderboard", "Leaderboard")}
            className={cn("w-full gap-y-0.5", LEADERBOARD_GRID_COMPACT)}
          >
            {head.map(renderRow)}
            {around.length > 0 && (
              <>
                <div
                  aria-hidden
                  className="col-span-full py-0.5 text-center font-heading text-[12px] leading-4 text-muted-foreground"
                >
                  ···
                </div>
                {around.map(renderRow)}
              </>
            )}
          </div>
        )}
        {hasMore && !loading && (
          <div className="flex justify-center px-(--card-pad) py-3">
            <Button
              variant="secondary"
              size="sm"
              className="text-[12px] lg:text-[13px]"
              onClick={() => setLimit((l) => l + MORE_ROWS)}
            >
              {t("tournament.showMore", "Show more")}
              <ChevronDown className="size-3.5 shrink-0" strokeWidth={2} />
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};
